import humps from 'lodash-humps-ts';
import { stringify } from 'qs';
import { array, number, object } from 'yup';

import { API_V1_BASE } from '@config';
import { snakes } from '@utils/lodash';
import { authAxios } from '@components/Auth';
import { PersonalAccidentQuote, PersonalAccidentQuoteCompare, PersonalAccidentQuoteList } from '@models/PersonalAccidentQuote';
import { QuoteMemberParams, QuoteOptionParams, quoteOptionParamsSchema } from '@models/Quote';
import { NumericString, WithParams } from '@utils/typescript';
import { formatFormData } from '@utils/formik';

export type GetAllPersonalAccidentQuoteParams = QuoteOptionParams;

export type ComparePersonalAccidentQuoteParams = QuoteOptionParams & WithParams<{
  personal_accident_plan_ids: number[];
}>;

export const comparePersonalAccidentQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    personalAccidentPlanIds: array().of(number())
  })
);

export type FindPersonalAccidentQuoteParams = QuoteOptionParams & WithParams<{
  personal_accident_plan_id: number;
}>;

export const findPersonalAccidentQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    personalAccidentPlanId: number(),
  }),
);

export type SendEmailPersonalAccidentQuoteParams = FindPersonalAccidentQuoteParams & {
  name: string;
  email: string;
};

export type CreatePersonalAccidentQuoteParams = FindPersonalAccidentQuoteParams & WithParams<{
  me: QuoteMemberParams;
  spouse?: QuoteMemberParams;
  sons?: QuoteMemberParams[];
  daughters?: QuoteMemberParams[];
}>;

export type GetPersonalAccidentQuoteParams = {
  orderNumber: string;
};

export type UpdatePersonalAccidentQuoteParams = GetPersonalAccidentQuoteParams & WithParams<{
  me: QuoteMemberParams;
  spouse?: QuoteMemberParams;
  sons?: QuoteMemberParams[];
  daughters?: QuoteMemberParams[];
}>;

type CustomPremium = WithParams<{
  premium: NumericString;
  discount_premium: NumericString;
}>

export type CustomPersonalAccidentQuoteParams = GetPersonalAccidentQuoteParams & WithParams<{
  me: CustomPremium;
  spouse?: CustomPremium;
  sons?: CustomPremium[];
  daughters?: CustomPremium[];
  admin_fee_amount: NumericString;
  note: string;
  proof?: File;
}>;

export class PersonalAccidentQuoteApi {
  static getAll = async ({ me, spouse, sons, daughters, ...rest }: GetAllPersonalAccidentQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/personal_accident_quotes`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as PersonalAccidentQuoteList[]);
  };

  static compare = async ({ me, spouse, sons, daughters, ...rest }: ComparePersonalAccidentQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/personal_accident_quotes`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as PersonalAccidentQuoteCompare[]);
  };

  static find = async ({ me, spouse, sons, daughters, ...rest }: FindPersonalAccidentQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/personal_accident_quotes/find`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as PersonalAccidentQuote);
  };

  static sendEmail = async ({ me, spouse, sons, daughters, ...rest }: SendEmailPersonalAccidentQuoteParams) => {
    return await authAxios.post(`${API_V1_BASE}/personal_accident_quotes/send_email`, snakes({ ...rest, members: { me, spouse, sons, daughters } }));
  };

  static create = async ({ me, spouse, sons, daughters, ...rest }: CreatePersonalAccidentQuoteParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/personal_accident_quotes`,
        snakes({ ...rest, members: { me, spouse, sons, daughters } }),
      )
      .then((response) => humps(response.data) as PersonalAccidentQuote);
  };

  static get = async ({ orderNumber }: GetPersonalAccidentQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/personal_accident_quotes/${encodeURIComponent(orderNumber)}`)
      .then((response) => humps(response.data) as PersonalAccidentQuote);
  };

  static update = async ({ orderNumber, me, spouse, sons, daughters, ...rest }: UpdatePersonalAccidentQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/personal_accident_quotes/${encodeURIComponent(orderNumber)}`,
        snakes({ ...rest, members: { me, spouse, sons, daughters } }),
      )
      .then((response) => humps(response.data) as PersonalAccidentQuote);
  };

  static custom = async ({ orderNumber, me, spouse, sons, daughters, ...rest }: CustomPersonalAccidentQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/personal_accident_quotes/${encodeURIComponent(orderNumber)}/custom`,
        formatFormData(snakes({ ...rest, members: { me, spouse, sons, daughters } })),
      )
      .then((response) => humps(response.data) as PersonalAccidentQuote);
  };
}
